import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Empty, Progress, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

import { UserContext } from '@/app/UserProvider';
import { getUsageStats } from '@/services/usageService';
import type { AccountInfo, UsageStats } from '@/services/usageService';
import { isInternalDomain } from '@/utils/const';

import styles from './UsageTab.module.less';
import AuditIcon from './images/audit.svg?react';
import UsageIcon from './images/usage.svg?react';

interface AuditRecord {
  key: string;
  date: string;
  model: string;
  tokenUsage: string;
  quotaUsage: string;
  remainingQuota: string;
}

interface OutletContextType {
  accountInfo: AccountInfo | null;
}

export function UsageTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountInfo } = useOutletContext<OutletContextType>();
  const { showPricing } = useContext(UserContext);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        const response = await getUsageStats({
          page: currentPage,
          pageSize,
        });

        if (response.status === 200 && response.data) {
          setUsageStats(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch usage stats:', error);
      }
    };

    fetchUsageStats();
  }, [currentPage, pageSize]);
  const auditColumns: ColumnsType<AuditRecord> = [
    {
      title: t('usageTab.table.date'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('usageTab.table.model'),
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: t('usageTab.table.tokenUsage'),
      dataIndex: 'tokenUsage',
      key: 'tokenUsage',
      align: 'right',
    },
    {
      title: t('usageTab.table.quotaUsage'),
      dataIndex: 'quotaUsage',
      key: 'quotaUsage',
      align: 'right',
    },
    // 仅在外部域名时显示剩余额度
    // ...(isInternalDomain()
    //   ? []
    //   : [
    //     {
    //       title: t('usageTab.table.remainingQuota'),
    //       dataIndex: 'remainingQuota',
    //       key: 'remainingQuota',
    //     },
    //   ]),
  ];

  // 将接口数据转换为表格数据格式
  const auditData: AuditRecord[] =
    usageStats?.usageList.map((item, index) => ({
      key: `${item.occurredAt}-${index}`,
      date: new Date(item.occurredAt).toLocaleString('zh-CN', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      model: `${item.modelName}`,
      tokenUsage: (Math.ceil(item.totalTokens * 100) / 100).toFixed(2),
      quotaUsage: (Math.ceil(item.creditUsed * 100) / 100).toFixed(2),
      remainingQuota: (Math.ceil(item.creditBalanceAfter * 100) / 100).toFixed(2),
    })) || [];

  // 分页配置
  const paginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: usageStats?.pagination.total || 0,
    showSizeChanger: true,
    showTotal: (total) => t('usageTab.pagination.total', { total }),
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
  };

  // 格式化数值：保留两位小数并向上进位
  const formatNumber = (num: number | undefined, fallbackText?: string): string => {
    if (num === undefined) return fallbackText ?? '-';
    return (Math.ceil(num * 100) / 100).toFixed(2);
  };

  // 计算剩余天数
  const getDaysUntilRenewal = () => {
    if (!accountInfo?.summary.renewAt) return 0;
    const now = Date.now();
    const diff = accountInfo.summary.renewAt - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // 格式化续订日期
  const formatRenewalDate = () => {
    if (!accountInfo?.summary.renewAt) return '';
    const date = new Date(accountInfo.summary.renewAt);
    return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`;
  };

  // free 计划相关状态
  const isFreePlan = accountInfo?.account.tierName?.toLowerCase() === 'free';
  const isExternal = !isInternalDomain();
  const daysUntilRenewal = getDaysUntilRenewal();

  // free 计划在对外环境下的两种状态
  const isFreeExpired = isFreePlan && isExternal && daysUntilRenewal === 0;
  const isFreeActive = isFreePlan && isExternal && daysUntilRenewal > 0;

  // 计算使用百分比
  const getUsagePercentage = () => {
    if (!accountInfo?.summary.creditTotal) return 0;
    return (accountInfo.summary.creditUsed / accountInfo.summary.creditTotal) * 100;
  };

  return (
    <div className="flex flex-1 flex-col gap-5">
      {/* 当前计划配额用量卡片 */}
      <div
        className="flex flex-col gap-[24px] overflow-x-auto rounded p-6  md:p-10"
        style={{
          background: 'linear-gradient(-60.75deg, #f7f7f7 0%, #ffeddf 100%)',
        }}
      >
        {/* 顶部标题和按钮 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[12px]">
            <UsageIcon width={18} height={15} />
            <h3
              className="text-base font-bold tracking-[-0.16px]"
              style={{
                fontFamily: 'Manrope',
                color: '#111314',
              }}
            >
              {t('usageTab.currentPlanUsage.title')}
            </h3>
          </div>
          {!isInternalDomain() && showPricing && (
            <div className="flex gap-4">
              <button
                className="flex h-[38px] w-[134px] items-center justify-center whitespace-nowrap rounded border border-[#111314] bg-[#111314] px-[34px] py-[8px] text-base font-medium leading-[22.4px] text-white transition-colors hover:opacity-90"
                onClick={() => navigate('/pricing')}
              >
                {t('usageTab.upgradePlan')}
              </button>
              {/* <button className="flex h-[38px] w-[134px] items-center justify-center rounded border border-[#111314] bg-transparent px-[34px] py-[8px] text-base font-medium leading-[22.4px] text-[#111314] transition-colors">
              {t('usageTab.buyQuota')}
            </button> */}
            </div>
          )}
        </div>

        {/* 数据显示区域 */}
        <div className="flex items-center justify-start gap-[3px] sm:gap-[4px] md:gap-[8px] lg:gap-[24px]">
          {/* 左侧：配额用量 */}
          <div className=" flex h-[48px] items-end justify-start gap-[8px]">
            <span
              className="text-[48px] leading-[48px] tracking-[-2.4px]"
              style={{
                fontFamily: 'Manrope',
                fontWeight: 600,
                color: '#111314',
              }}
            >
              {formatNumber(accountInfo?.summary.creditAvailable)}
            </span>
            <div className="flex h-[37px] flex-col items-center justify-center">
              <div className="flex h-[29px] flex-col">
                <span
                  className="text-[12px] font-semibold leading-[130%] text-[#111314]"
                  style={{
                    fontFamily: 'Manrope',
                  }}
                >
                  {formatNumber(accountInfo?.summary.creditUsed)}/
                  {formatNumber(
                    accountInfo?.summary.creditTotal,
                    t('usageTab.currentPlanUsage.noLimit'),
                  )}
                </span>
                <span
                  className="text-[12px] font-normal leading-[130%] text-[#111314] opacity-80"
                  style={{
                    fontFamily: 'Manrope',
                  }}
                >
                  used
                </span>
              </div>
            </div>
          </div>

          {/* 分隔线 + 右侧更新时间（free 到期时隐藏） */}
          {!isFreeExpired && (
            <>
              <div className="h-[43px] w-px bg-[#111314]" />

              {/* 右侧：更新时间 */}
              <div className="flex h-[48px] w-[133px] items-end justify-start gap-[10px] pr-[11px]">
                <span
                  className="text-[48px] leading-[48px] tracking-[-2.4px]"
                  style={{
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    color: '#111314',
                  }}
                >
                  {getDaysUntilRenewal()}
                </span>
                <div className="flex h-[40px] w-[64px] flex-col items-end px-0 py-[4px]">
                  <div className="flex h-[32px] w-[64px] flex-col items-start justify-between">
                    <span
                      className="whitespace-nowrap text-[12px] font-semibold leading-[130%] text-[#111314]"
                      style={{
                        fontFamily: 'Manrope',
                      }}
                    >
                      {t('usageTab.renewalDate.renewalTime')}
                    </span>
                    <span
                      className="text-[12px] font-normal leading-[130%] text-[#111314] opacity-80"
                      style={{
                        fontFamily: 'Manrope',
                      }}
                    >
                      {t('usageTab.renewalDate.daysAfter')}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 进度条 */}
        <Progress
          percent={getUsagePercentage()}
          showInfo={false}
          strokeColor="#111314"
          trailColor="#C9C9CA"
          strokeWidth={3}
          style={{ opacity: 0.5, height: '3px', lineHeight: '3px' }}
        />

        {/* 底部说明文字 */}
        <div className="flex flex-col justify-between gap-12 md:flex-row">
          {/* 左侧 */}
          <p className="text-[12px] leading-[130%] text-[#111314] opacity-80">
            <span className="text-[#111314]">
              {t('usageTab.currentPlanUsage.descriptionPrefix')}
            </span>{' '}
            <a
              href="https://www.codeflicker.ai/docs/feats/others/credits.html"
              className="!text-[#111314] underline hover:!text-[#111314] hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {t('usageTab.currentPlanUsage.documentation')}
            </a>
            <span className="text-[#111314]">
              {t('usageTab.currentPlanUsage.descriptionSuffix')}
            </span>
          </p>
          {/* 右侧 */}
          <p className="text-[12px] leading-[130%] text-[#111314] opacity-80">
            {isFreeExpired ? (
              t('usageTab.renewalDate.freeExpiredHint')
            ) : (
              <>
                {t('usageTab.renewalDate.descriptionPrefix')}
                <span className="text-[#111314]">
                  {' '}
                  {getDaysUntilRenewal()}
                  {t('usageTab.renewalDate.daysAfter')}({formatRenewalDate() || '-'})
                  {t('usageTab.renewalDate.descriptionSuffix')}
                </span>
                {isFreeActive && <span>{t('usageTab.renewalDate.freeNoRenewalHint')}</span>}
              </>
            )}
          </p>
        </div>
      </div>

      {/* 积分消耗审计卡片 */}
      <div className="flex flex-col gap-4 rounded bg-[#ffffff] py-4">
        <div className="flex items-center gap-[6px]">
          <AuditIcon />
          <h3
            className="text-base font-semibold tracking-[-0.16px]"
            style={{
              fontFamily: 'Manrope',
              color: '#111314',
            }}
          >
            {t('usageTab.audit.title')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <Table
            columns={auditColumns}
            dataSource={auditData}
            pagination={paginationConfig}
            size="small"
            className={styles.usageAuditTable}
            style={{ fontSize: '12px' }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.noData')} />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
